// src/components/digital_twin/DataOverlay.js
// Enhanced Data Overlay with enterprise features
// All existing logic preserved + new enterprise enhancements
// --- UPDATE: v2.0
//   • AI/ML strip: predicted-failure counts from the bulk health model and the
//     active simulation's impact (damage, flooding, restoration) in one row
//   • memoised (React.memo) — the page re-renders on every telemetry flush
//   • hooks hoisted out of JSX; export via Blob (data-URI exports truncate
//     large payloads in some browsers) with URL revocation
//   • activeSensors counts only sensors with a reading
// --- END UPDATE

import React, { useState, useMemo, memo, useCallback } from 'react';
import {
  Box, Flex, Stat, StatLabel, StatNumber, SimpleGrid,
  IconButton, Collapse, Text, Icon, Button, StatHelpText, StatArrow,
  useColorModeValue, HStack, Badge, Progress, Tooltip, Menu,
  MenuButton, MenuList, MenuItem, VStack, Divider,
} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { 
  FaInfoCircle, FaChartLine, FaExpand, FaDownload, FaCog,
  FaServer, FaBell, FaExclamationTriangle, FaCity, FaSignal,
} from 'react-icons/fa';
import { MdMoreVert } from 'react-icons/md';
// --- UPDATE
import { FaBrain, FaWind } from 'react-icons/fa';
// --- END UPDATE
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Helper functions (preserved)
const getAlertSeverity = (count) => {
  if (count > 10) return 'high';
  if (count > 0) return 'medium';
  return 'low';
};

const getBgColor = (severity, colorMode) => {
  const colors = {
    high: colorMode === 'light' ? 'rgba(200, 30, 30, 0.95)' : 'rgba(120, 0, 0, 0.95)',
    medium: colorMode === 'light' ? 'rgba(200, 100, 30, 0.95)' : 'rgba(120, 60, 0, 0.95)',
    low: colorMode === 'light' ? 'rgba(70, 85, 100, 0.95)' : 'rgba(45, 55, 72, 0.95)'
  };
  return colors[severity];
};

const DataOverlay = ({ 
  assets = [], 
  alerts = [], 
  issues = [], 
  sensorReadings = {}, 
  onExpandDetails, 
  isLive, 
  onGoLive,
  onMinimizeChange, // NEW: fires with (isMinimized: boolean) whenever the panel toggles
  // --- UPDATE
  aiRisk = null,          // { critical, warning, scored }
  simulation = null,      // { title, scenario, summary }
  onOpenSimulation,       // focus the simulation panel
  // --- END UPDATE
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const colorMode = useColorModeValue('light', 'dark');

  // UPDATED: notify parent whenever minimize state changes
  const handleToggle = () => {
    const next = !isMinimized;
    setIsMinimized(next);
    onMinimizeChange?.(next);
  };
  
  // Computed values (preserved)
  // --- UPDATE: count sensors that actually have a reading
  const activeSensors = useMemo(() => Object.values(sensorReadings).filter(Boolean).length, [sensorReadings]);
  // --- END UPDATE
  const alertSeverity = getAlertSeverity(alerts.length);
  const bgColor = getBgColor(alertSeverity, colorMode);
  
  // Asset counts by type (preserved)
  const assetCountsByType = useMemo(() => {
    return assets.reduce((acc, asset) => {
      const type = asset.type || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  }, [assets]);

  // NEW: Enhanced statistics
  const advancedStats = useMemo(() => {
    const criticalAlerts = alerts.filter(a => a.severity === 'critical').length;
    const highPriorityIssues = issues.filter(i => i.priority === 'high').length;
    const operationalAssets = assets.filter(a => a.status === 'operational').length;
    const healthPercentage = assets.length > 0 
      ? Math.round((operationalAssets / assets.length) * 100) 
      : 0;

    return {
      criticalAlerts,
      highPriorityIssues,
      operationalAssets,
      healthPercentage,
      totalSensorReadings: Object.values(sensorReadings).filter(Boolean).length,
    };
  }, [alerts, issues, assets, sensorReadings]);

  // NEW: Export data handler
  const handleExportData = useCallback(() => {
    const exportData = {
      timestamp: new Date().toISOString(),
      summary: {
        totalAssets: assets.length,
        activeAlerts: alerts.length,
        openIssues: issues.length,
        activeSensors,
        systemHealth: advancedStats.healthPercentage,
      },
      assetsByType: assetCountsByType,
      alerts: alerts.map(a => ({ id: a.id, severity: a.severity, message: a.message })),
      // --- UPDATE
      aiRisk,
      simulation: simulation ? { title: simulation.title, scenario: simulation.scenario, summary: simulation.summary } : null,
      // --- END UPDATE
    };

    // --- UPDATE: Blob export
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', url);
    linkElement.setAttribute('download', `system-overview-${Date.now()}.json`);
    linkElement.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    // --- END UPDATE
  }, [assets.length, alerts, issues.length, activeSensors, advancedStats.healthPercentage, assetCountsByType, aiRisk, simulation]);

  // Theme colors
  const textColor = useColorModeValue('white', 'white');
  const iconColor = useColorModeValue('whiteAlpha.900', 'whiteAlpha.900');
  const badgeBg = useColorModeValue('whiteAlpha.300', 'whiteAlpha.200');
  // --- UPDATE: hoisted from JSX
  const menuBg = useColorModeValue('white', 'gray.800');
  const menuColor = useColorModeValue('gray.800', 'white');
  const simSummary = simulation?.summary || null;
  // --- END UPDATE

  return (
    <MotionBox
      position="absolute"
      bottom="0"
      left="0"
      right="0"
      bg={bgColor}
      color={textColor}
      borderTopRadius="xl"
      boxShadow="0 -8px 24px rgba(0,0,0,0.4)"
      zIndex={10}
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header Bar */}
      <Flex align="center" p={4} borderBottomWidth={isMinimized ? '0' : '1px'} borderColor="whiteAlpha.300">
        <HStack spacing={3} flex={1}>
          <Icon as={FaInfoCircle} boxSize={5} color={iconColor} />
          <VStack align="start" spacing={0}>
            <Text fontWeight="800" fontSize="md">System Overview</Text>
            {!isMinimized && (
              <HStack spacing={2} fontSize="xs" opacity={0.8}>
                <HStack spacing={1}>
                  <Icon as={FaServer} boxSize={3} />
                  <Text>{assets.length} Assets</Text>
                </HStack>
                <Text>•</Text>
                <HStack spacing={1}>
                  <Icon as={FaSignal} boxSize={3} />
                  <Text>{activeSensors} Sensors</Text>
                </HStack>
                {advancedStats.criticalAlerts > 0 && (
                  <>
                    <Text>•</Text>
                    <Badge colorScheme="red" fontSize="2xs" px={2} borderRadius="full">
                      {advancedStats.criticalAlerts} Critical
                    </Badge>
                  </>
                )}
              </HStack>
            )}
          </VStack>
        </HStack>

        <HStack spacing={2}>
          {/* Live Indicator / Go Live Button */}
          {isLive ? (
            <MotionFlex
              align="center"
              gap={2}
              px={3}
              py={1}
              bg="green.500"
              borderRadius="full"
              animate={{ opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Box w="2" h="2" bg="white" borderRadius="full" />
              <Text fontSize="xs" fontWeight="700">LIVE</Text>
            </MotionFlex>
          ) : (
            <Button 
              size="sm" 
              colorScheme="green" 
              onClick={onGoLive}
              leftIcon={<FaSignal />}
              fontWeight="700"
              boxShadow="md"
            >
              Go Live
            </Button>
          )}

          {/* Advanced Toggle */}
          <Tooltip label="Advanced Statistics" hasArrow>
            <IconButton
              icon={<FaChartLine />}
              size="sm"
              variant={showAdvanced ? "solid" : "outline"}
              colorScheme="whiteAlpha"
              onClick={() => setShowAdvanced(!showAdvanced)}
              aria-label="Advanced stats"
            />
          </Tooltip>

          {/* More Options Menu */}
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<MdMoreVert />}
              size="sm"
              variant="ghost"
              color={textColor}
              _hover={{ bg: 'whiteAlpha.300' }}
              aria-label="More options"
            />
            <MenuList bg={menuBg} color={menuColor}>
              <MenuItem icon={<FaExpand />} onClick={onExpandDetails}>
                View Full Details
              </MenuItem>
              <MenuItem icon={<FaDownload />} onClick={handleExportData}>
                Export Data
              </MenuItem>
              <MenuItem icon={<FaCog />}>
                Settings
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Minimize/Expand Toggle */}
          <IconButton
            aria-label={isMinimized ? 'Show Overview' : 'Hide Overview'}
            icon={isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />}
            size="sm"
            onClick={handleToggle}
            variant="ghost"
            color={textColor}
            _hover={{ bg: 'whiteAlpha.300' }}
          />
        </HStack>
      </Flex>

      {/* Main Content */}
      <AnimatePresence>
        {!isMinimized && (
          <MotionBox
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* --- UPDATE: AI / ML + simulation strip */}
            {(aiRisk || simulation) && (
              <HStack px={4} pt={3} spacing={2} flexWrap="wrap">
                {aiRisk && (
                  <HStack spacing={2} bg={badgeBg} px={3} py={1.5} borderRadius="full">
                    <Icon as={FaBrain} boxSize={3} />
                    <Text fontSize="xs" fontWeight="800">AI predicted</Text>
                    <Badge colorScheme="red" fontSize="2xs">{aiRisk.critical} critical</Badge>
                    <Badge colorScheme="orange" fontSize="2xs">{aiRisk.warning} warning</Badge>
                    <Text fontSize="2xs" opacity={0.8}>{aiRisk.scored} scored</Text>
                  </HStack>
                )}
                {simulation && (
                  <HStack as="button" type="button" onClick={onOpenSimulation} spacing={2} bg="orange.500" px={3} py={1.5} borderRadius="full" _hover={{ bg: 'orange.400' }}>
                    <Icon as={FaWind} boxSize={3} />
                    <Text fontSize="xs" fontWeight="800">SIM: {simulation.title}</Text>
                    {simSummary?.moderateOrWorse != null ? (
                      <>
                        <Badge colorScheme="red" fontSize="2xs">{simSummary.moderateOrWorse} damaged</Badge>
                        <Badge colorScheme="blue" fontSize="2xs">{simSummary.assetsFlooded ?? 0} flooded</Badge>
                      </>
                    ) : (
                      <Badge colorScheme="red" fontSize="2xs">{simSummary?.totalAffected ?? 0} affected</Badge>
                    )}
                  </HStack>
                )}
              </HStack>
            )}
            {/* --- END UPDATE */}

            {/* System Health Bar */}
            <Box px={4} pt={3}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="xs" fontWeight="700" opacity={0.9}>
                  System Health
                </Text>
                <Text fontSize="xs" fontWeight="700">
                  {advancedStats.healthPercentage}%
                </Text>
              </HStack>
              <Progress 
                value={advancedStats.healthPercentage} 
                size="sm" 
                colorScheme={
                  advancedStats.healthPercentage > 80 ? 'green' :
                  advancedStats.healthPercentage > 50 ? 'yellow' : 'red'
                }
                borderRadius="full"
                bg="whiteAlpha.300"
              />
            </Box>

            {/* Primary Stats */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} p={4}>
              <Stat>
                <StatLabel fontSize="xs" opacity={0.8}>Total Assets</StatLabel>
                <StatNumber fontSize="2xl" fontWeight="800">{assets.length}</StatNumber>
                <StatHelpText fontSize="2xs" opacity={0.7}>
                  <Icon as={FaCity} mr={1} />
                  {advancedStats.operationalAssets} operational
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel fontSize="xs" opacity={0.8}>Active Alerts</StatLabel>
                <StatNumber fontSize="2xl" fontWeight="800" color="red.300">
                  {alerts.length}
                </StatNumber>
                <StatHelpText fontSize="2xs" opacity={0.7}>
                  <StatArrow type="increase" />
                  {advancedStats.criticalAlerts} critical
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel fontSize="xs" opacity={0.8}>Open Issues</StatLabel>
                <StatNumber fontSize="2xl" fontWeight="800" color="yellow.300">
                  {issues.length}
                </StatNumber>
                <StatHelpText fontSize="2xs" opacity={0.7}>
                  <Icon as={FaExclamationTriangle} mr={1} />
                  {advancedStats.highPriorityIssues} high priority
                </StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel fontSize="xs" opacity={0.8}>Live Sensors</StatLabel>
                <StatNumber fontSize="2xl" fontWeight="800">{activeSensors}</StatNumber>
                <StatHelpText fontSize="2xs" opacity={0.7}>
                  <Icon as={FaSignal} mr={1} />
                  {advancedStats.totalSensorReadings} readings
                </StatHelpText>
              </Stat>
            </SimpleGrid>

            {/* Advanced Stats */}
            <Collapse in={showAdvanced} animateOpacity>
              <Box px={4} pb={4}>
                <Divider borderColor="whiteAlpha.300" mb={4} />
                <Text fontSize="xs" fontWeight="700" mb={3} opacity={0.9}>
                  Asset Distribution
                </Text>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                  {Object.entries(assetCountsByType).map(([type, count]) => (
                    <Box 
                      key={type} 
                      p={3} 
                      bg="whiteAlpha.200" 
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="whiteAlpha.300"
                    >
                      <Text fontSize="2xs" fontWeight="600" opacity={0.8} textTransform="uppercase">
                        {type}
                      </Text>
                      <Text fontSize="xl" fontWeight="800" mt={1}>
                        {count}
                      </Text>
                      <Progress 
                        value={(count / assets.length) * 100} 
                        size="xs" 
                        colorScheme="whiteAlpha"
                        mt={2}
                        borderRadius="full"
                        bg="whiteAlpha.300"
                      />
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </Collapse>
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  );
};

// --- UPDATE: memoised
export default memo(DataOverlay);
// --- END UPDATE